import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip
} from '@mui/material';
import { Refresh, Work, People, Business, Description, Report, TrendingUp } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/rojgar';

const RojgarDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentProviders, setRecentProviders] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, jobsRes, providersRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/stats`),
        axios.get(`${API_BASE}/admin/jobs`, { params: { limit: 5 } }),
        axios.get(`${API_BASE}/admin/providers`, { params: { limit: 5 } })
      ]);
      
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (jobsRes.data.success) {
        setRecentJobs(jobsRes.data.data);
      }
      if (providersRes.data.success) {
        setRecentProviders(providersRes.data.data);
      }
    } catch (err) {
      setError('Error fetching dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    { label: 'Total Jobs', value: stats?.totalJobs || 0, color: '#2196F3', icon: <Work sx={{ fontSize: 40 }} /> },
    { label: 'Active Jobs', value: stats?.activeJobs || 0, color: '#4CAF50', icon: <Work sx={{ fontSize: 40 }} /> },
    { label: 'Total Seekers', value: stats?.totalSeekers || 0, color: '#9C27B0', icon: <People sx={{ fontSize: 40 }} /> },
    { label: 'Total Providers', value: stats?.totalProviders || 0, color: '#FF9800', icon: <Business sx={{ fontSize: 40 }} /> },
    { label: 'Pending KYC', value: stats?.pendingKYC || 0, color: '#F44336', icon: <Description sx={{ fontSize: 40 }} /> },
    { label: 'Applications', value: stats?.totalApplications || 0, color: '#00BCD4', icon: <TrendingUp sx={{ fontSize: 40 }} /> },
    { label: 'Pending Reports', value: stats?.pendingReports || 0, color: '#E91E63', icon: <Report sx={{ fontSize: 40 }} /> },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF5722' }}>
          Rojgar Dashboard
        </Typography>
        <IconButton onClick={fetchData} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ bgcolor: stat.color, color: 'white', height: '100%' }}>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                  <Typography variant="body1">{stat.label}</Typography>
                </Box>
                {stat.icon}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Jobs */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#FF5722' }}>
              Recent Jobs
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Title</b></TableCell>
                    <TableCell><b>Company</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentJobs.map((job) => (
                    <TableRow key={job._id} hover>
                      <TableCell>{job.title}</TableCell>
                      <TableCell>{job.providerId?.companyName || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={job.status} size="small" color={job.status === 'active' ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentJobs.length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center">No jobs</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Recent Providers */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#FF5722' }}>
              Recent Providers
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Company</b></TableCell>
                    <TableCell><b>City</b></TableCell>
                    <TableCell><b>KYC Status</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentProviders.map((provider) => (
                    <TableRow key={provider._id} hover>
                      <TableCell>{provider.companyName || 'N/A'}</TableCell>
                      <TableCell>{provider.city || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={provider.kycStatus || 'not_started'} 
                          size="small" 
                          color={provider.kycStatus === 'approved' ? 'success' : provider.kycStatus === 'submitted' ? 'warning' : 'default'} 
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentProviders.length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center">No providers</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RojgarDashboard;
